import { config } from '@/forms/config';
import { addressLinkFieldId } from '@/forms/utils/addressLink';
import type {
  ArrayFormField,
  CheckboxGroupFormField,
  ConditionalFieldsGroupFormField,
  DateRangeFormField,
  FieldsGroupFormField,
  FileFormField,
  FormField,
  OfficeHoursFormField,
  RadioGridFormField,
  StagedFieldsGroupFormField,
} from '@/forms/typings';
import { FormFieldType } from '@/forms/typings';

export type FormFieldHasFormikValueOptions = {
  /** When true, a child explicitly marked Not Applicable counts as having data. */
  treatNotApplicableAsValue?: boolean;
};

const hasPrimitiveValue = (value: unknown): boolean => {
  if (value === undefined || value === null || value === '' || value === false)
    return false;

  if (Array.isArray(value)) return value.length > 0;

  return true;
};

/** True when the provider has answered this field (or any descendant) in the current Formik values. */
export const formFieldHasFormikValue = (
  field: FormField,
  root: Record<string, unknown>,
  options?: FormFieldHasFormikValueOptions,
): boolean => {
  if (
    options?.treatNotApplicableAsValue &&
    'notApplicableField' in field &&
    field.notApplicableField &&
    root[field.notApplicableField.fieldId] === true
  )
    return true;

  switch (field.type) {
    case FormFieldType.File:
      return (
        Number(
          root[
            `${(field as FileFormField).fieldId}${config.minFilesCountFieldSuffix}`
          ],
        ) > 0
      );

    case FormFieldType.QuestionWithDetails: {
      // Any answer counts as data; details subtrees are only reachable after one.
      const answer =
        root[`${field.fieldId}${config.questionWithDetailsFieldSuffix}`];
      return answer === 'yes' || answer === 'no';
    }

    case FormFieldType.Checkbox:
      return root[field.fieldId] === true;

    case FormFieldType.DateRange:
      return (field as DateRangeFormField).fields.some(dateField =>
        hasPrimitiveValue(root[dateField.fieldId]),
      );

    case FormFieldType.OfficeHours:
      return Object.values((field as OfficeHoursFormField).days).some(day =>
        day.fields.some(
          dayField =>
            hasPrimitiveValue(root[dayField.fieldId]) ||
            root[`${dayField.fieldId}${config.officeHoursOnCallFieldSuffix}`] ===
              true,
        ),
      );

    case FormFieldType.CheckboxGroup:
      return (
        hasPrimitiveValue(root[field.fieldId]) ||
        (field as CheckboxGroupFormField).fields.some(
          checkbox => root[checkbox.fieldId] === true,
        )
      );

    case FormFieldType.RadioGrid:
      return (field as RadioGridFormField).fields.some(radioGroup =>
        hasPrimitiveValue(root[radioGroup.fieldId]),
      );

    case FormFieldType.FieldsGroup:
      return (
        // An answered address choice is a value, as the server's HasAnyValue counts it.
        hasPrimitiveValue(root[addressLinkFieldId(field.fieldId)]) ||
        (field as FieldsGroupFormField).fields.some(inner =>
          formFieldHasFormikValue(inner, root, options),
        )
      );

    case FormFieldType.Array:
      return (field as ArrayFormField).fields.some(inner =>
        formFieldHasFormikValue(inner, root, options),
      );

    case FormFieldType.StagedFieldsGroup: {
      const staged = field as StagedFieldsGroupFormField;
      return [...staged.firstStageFields, ...staged.otherFields].some(inner =>
        formFieldHasFormikValue(inner, root, options),
      );
    }

    case FormFieldType.ConditionalFieldsGroup: {
      const conditional = field as ConditionalFieldsGroupFormField;
      return (
        formFieldHasFormikValue(conditional.conditionField, root, options) ||
        conditional.children.some(inner =>
          formFieldHasFormikValue(inner, root, options),
        )
      );
    }

    case FormFieldType.Login:
      return false;

    default:
      // Singles (Input, TextArea, Select, RadioGroup, Date, Time) store the raw value.
      return hasPrimitiveValue(root[field.fieldId]);
  }
};
